import { useEffect, useMemo, useState } from "react";
import { reconstructFestivalDays } from "@/lib/doorsForDay";

interface BriefingDateFields {
  festival_date_start?: string | null;
  festival_date_end?: string | null;
  doors_open?: string | null;
  doors_close?: string | null;
}

type DayDoors = {
  event_date: string;
  doors_open: string | null;
  doors_close: string | null;
};

/**
 * Resolve a briefing's day list ("yyyy-mm-dd") and track the selected
 * day for the multi-day Day 1 / Day 2 / Day 3 selectors. Shared by the
 * review/publish and portal briefing pages so the day-derivation and
 * default-selection logic live in one place.
 */
export function useFestivalDays(
  briefing: BriefingDateFields | null | undefined,
  dayDoors: DayDoors[],
) {
  const festivalDates = useMemo(() => {
    if (!briefing?.festival_date_start || !briefing?.festival_date_end) return [];
    return reconstructFestivalDays({
      startDate: briefing.festival_date_start,
      endDate: briefing.festival_date_end,
      perDayDoors: dayDoors,
      fallback: {
        doors_open: briefing.doors_open ?? null,
        doors_close: briefing.doors_close ?? null,
      },
    }).map((d) => d.date);
  }, [briefing, dayDoors]);

  const [selectedDate, setSelectedDate] = useState("");
  useEffect(() => {
    if (festivalDates.length && !festivalDates.includes(selectedDate)) {
      setSelectedDate(festivalDates[0]);
    }
  }, [festivalDates, selectedDate]);

  return {
    festivalDates,
    isMultiDay: festivalDates.length > 1,
    selectedDate,
    setSelectedDate,
  };
}
